import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Download, Lock, CheckCircle2, ShieldCheck, Zap, Database,
  Network, Users, Grid3x3, KeyRound, UserCheck, CalendarDays, GraduationCap,
  Wallet, FileText, ClipboardCheck, UserCog, Server, Eye, GitBranch
} from 'lucide-react';
import loginImg from '../assets/case-hr/login.jpg';
import dashboardImg from '../assets/case-hr/dashboard.jpg';
import orgImg from '../assets/case-hr/org.jpg';
import employeesImg from '../assets/case-hr/employees.jpg';
import screeningImg from '../assets/case-hr/screening.jpg';
import trainingImg from '../assets/case-hr/training.jpg';
import offboardingImg from '../assets/case-hr/offboarding.jpg';

const CaseStudyHR = () => {
  const before = [
    'No single source of truth — the same person\u2019s data lived in several places, often contradicting each other.',
    'Hard to see the organisation — no live view of departments, reporting lines or roles; org charts were redrawn by hand in slides.',
    'Painful updates — one change (promotion, transfer, new hire) had to be edited in many documents, so data drifted out of sync.',
    'Invisible access rights — no central record of who has access to which system, a direct ISO 27001 / audit risk.',
    'Manual compliance — screening, onboarding/offboarding and document sign-offs tracked in ad-hoc files, making audits slow and error-prone.',
    'Silos — leave balances in Absentify, budget/payroll in Excel; neither talked to HR records.',
  ];

  const modules = [
    { icon: <Network size={18} />, title: 'Organisation Structure', text: 'Live, editable hierarchy (executives → departments → roles → people) with an interactive visual chart and ISO-scope tagging; create, edit, move and delete in place.' },
    { icon: <Users size={18} />, title: 'Employees & Contractors', text: 'Full profiles (manager, start/end dates, birth date), role/department transfer history, per-person documents and access rights.' },
    { icon: <Grid3x3 size={18} />, title: 'RACI Matrix', text: 'Responsible / Accountable / Consulted / Informed mapping for governance and ISO evidence.' },
    { icon: <KeyRound size={18} />, title: 'Systems & Access Rights', text: 'Register of systems and exactly who can access each — the core of ISO 27001 access control.' },
    { icon: <UserCheck size={18} />, title: 'Screening & On/Off-boarding', text: 'Checklist-driven workflows, including screening (A.6.1), onboarding and offboarding.' },
    { icon: <CalendarDays size={18} />, title: 'Leave Management', text: 'Vacation, sick and business trips; custom balances (24 base days + carryover); manager approvals; team calendar; CSV import from Absentify.' },
    { icon: <GraduationCap size={18} />, title: 'Training', text: 'Courses and completions synced from an external training system\u2019s API.' },
    { icon: <Wallet size={18} />, title: 'Payroll & Quarterly Budget', text: 'Payroll and quarterly budget tracking held alongside HR records.' },
    { icon: <FileText size={18} />, title: 'Documents', text: 'Per-person and company documents stored in GridFS, uploaded and streamed back through the API.' },
    { icon: <ClipboardCheck size={18} />, title: 'Compliance & Audit Logs', text: 'Compliance dashboard and audit logs that make audits a click.' },
    { icon: <UserCog size={18} />, title: 'User Management & Auth', text: 'Accounts, roles and authentication with role-based permissions.' },
  ];

  const apiGroups = [
    ['Auth / Users', 'POST /api/auth/login · GET /api/auth/me · POST /api/auth/users/{id}/reset-password'],
    ['Employees', 'GET/POST/PUT/DELETE /api/employees · POST /api/employees/{id}/transfer'],
    ['Organisation', 'GET/PUT /api/organization · POST /api/organization/departments/{id}/roles'],
    ['Systems / Access', 'GET/POST/DELETE /api/systems/access/{employee_id}'],
    ['Leave', 'POST /api/leave/requests · .../approve · GET /api/leave/calendar · POST /api/leave/balance/import-csv'],
    ['Documents / Files', 'POST /api/files/upload/{folder} · GET /api/files/download/{id}'],
    ['Bulk Import', 'POST /api/bulk-import (CSV)'],
    ['Also', 'RACI · Screening · Onboarding · Offboarding · Payroll · Quarterly Budget'],
  ];

  const externals = [
    { title: 'Training system API', text: 'Pulled over HTTP (httpx) and synced into the central database.' },
    { title: 'Absentify (CSV)', text: 'Existing leave balances migrated in via a CSV pipeline.' },
    { title: 'Document storage (GridFS)', text: 'Uploads stored and streamed back through the API.' },
  ];

  const benefits = [
    { icon: <Database className="text-brand-primary" size={22} />, title: 'One source of truth', text: 'Consistent data everywhere — edit once, reflected system-wide.' },
    { icon: <Eye className="text-brand-primary" size={22} />, title: 'Instant visibility', text: 'Always-current org chart and dashboards replace manual slides.' },
    { icon: <Zap className="text-brand-primary" size={22} />, title: 'Effortless updates', text: 'A transfer or promotion is one action (with history), not many edits.' },
    { icon: <ClipboardCheck className="text-brand-primary" size={22} />, title: 'Audit-ready ISO 27001', text: 'Centralised screening, access register, RACI, checklists and audit log.' },
    { icon: <ShieldCheck className="text-brand-primary" size={22} />, title: 'Stronger governance', text: 'Role-based access plus documented system-access grants.' },
    { icon: <CheckCircle2 className="text-brand-primary" size={22} />, title: 'Time & error reduction', text: 'Bulk CSV import, leave migration and training sync remove manual entry.' },
  ];

  const stack = ['React', 'FastAPI', 'MongoDB (Motor)', 'GridFS', 'JWT (python-jose)', 'bcrypt', 'httpx', 'Pydantic', 'REST with RBAC'];

  const screens = [
    { title: 'Secure Login', caption: 'Sign-in to the HR portal with ISO 27001 and GDPR compliance badges.', image: loginImg },
    { title: 'Dashboard', caption: 'HR overview — headcount, pending screenings, overdue trainings and overall ISO 27001 compliance score.', image: dashboardImg },
    { title: 'Organisation Structure', caption: 'Live, editable department hierarchy with ISO-scope tagging and per-department heads.', image: orgImg },
    { title: 'Employees & Contractors', caption: 'Central profiles with department, role, screening and compliance status, plus bulk import/export.', image: employeesImg },
    { title: 'HR Screening (A.6.1)', caption: 'Background verification with per-candidate progress tracking.', image: screeningImg },
    { title: 'Training & Awareness (A.6.3)', caption: 'Security-awareness training synced from the training platform, with per-employee progress.', image: trainingImg },
    { title: 'Offboarding (A.6.5)', caption: 'Secure exit process with an ISO 27001 security-requirements checklist.', image: offboardingImg },
  ];

  return (
    <div className="min-h-screen bg-bg-page" data-testid="case-study-hr">
      {/* Simple top bar */}
      <header className="sticky top-0 z-50 bg-bg-page/90 backdrop-blur-md border-b border-border-medium">
        <div className="container mx-auto px-8 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 nav-link" data-testid="case-back-link">
            <ArrowLeft size={18} />
            Back to portfolio
          </Link>
          <a
            href="/Anna-Krat-CV.pdf"
            download="Anna-Krat-CV.pdf"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-primary text-white text-sm font-medium hover:bg-brand-hover transition-colors"
            data-testid="case-download-cv-btn"
          >
            <Download size={16} />
            CV
          </a>
        </div>
      </header>

      <main className="container mx-auto px-8 py-16 max-w-5xl">
        {/* Title */}
        <div className="mb-12">
          <div className="flex items-center flex-wrap gap-3 mb-5">
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-brand-soft text-brand-primary border border-indigo-100">
              Full-stack • Live • Private company system
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs text-text-muted">
              <Lock size={13} /> Contains personal data — public login not available
            </span>
          </div>
          <h1 className="heading-1 text-text-primary mb-5">HR & Organisation Management Platform</h1>
          <p className="body-large text-text-secondary max-w-3xl">
            A centralised, web-based platform that is the single source of truth for the whole employee lifecycle and the
            company&apos;s ISO 27001 compliance. People, departments, roles, access rights, leave, documents and compliance
            all live in one database, edited through one interface with role-based permissions.
          </p>

          {/* Quick stats */}
          <div className="grid sm:grid-cols-3 gap-6 mt-10">
            {[
              { icon: <Database className="text-brand-primary" size={22} />, big: 'Single source of truth', small: 'People, org, access, leave & compliance in one database' },
              { icon: <ShieldCheck className="text-brand-primary" size={22} />, big: 'ISO 27001 audit-ready', small: 'Screening, access register, RACI & audit logs' },
              { icon: <Zap className="text-brand-primary" size={22} />, big: 'One-action updates', small: 'Transfers & promotions with full history' },
            ].map((s, i) => (
              <div key={i} className="team-card p-6" data-testid={`case-stat-${i}`}>
                <div className="mb-3">{s.icon}</div>
                <div className="heading-5 text-text-primary mb-1">{s.big}</div>
                <div className="body-small text-text-secondary">{s.small}</div>
              </div>
            ))}
          </div>
        </div>

        {/* My role */}
        <section className="team-card p-8 mb-16">
          <h2 className="heading-4 text-text-primary mb-3">My role</h2>
          <p className="body-medium">
            Delivered end-to-end, from idea to implementation — workflow design, UX/UI, requirements, modular full-stack
            build (independent modules with their own API and data model over one central database), integrations,
            role-based security, testing and deployment — built by me with AI agents.
          </p>
        </section>

        {/* The situation before */}
        <section className="mb-16">
          <h2 className="heading-3 text-text-primary mb-8">The situation before</h2>
          <ul className="grid md:grid-cols-2 gap-4">
            {before.map((b, i) => (
              <li key={i} className="team-card p-5 flex items-start gap-3">
                <div className="w-2 h-2 bg-brand-primary rounded-full flex-shrink-0 mt-2" />
                <span className="body-small text-text-primary">{b}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* How it works - modules */}
        <section className="mb-16">
          <h2 className="heading-3 text-text-primary mb-3">How it works</h2>
          <p className="body-medium mb-8 max-w-3xl">A modular full-stack application — each business area is an independent module with its own API and data model, sharing one central database.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {modules.map((m, i) => (
              <div key={i} className="team-card p-6" data-testid={`case-module-${i}`}>
                <div className="flex items-center gap-2 mb-2 text-brand-primary">
                  {m.icon}
                  <h3 className="heading-6 text-text-primary">{m.title}</h3>
                </div>
                <p className="body-small text-text-secondary">{m.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Screens gallery */}
        <section className="mb-16">
          <h2 className="heading-3 text-text-primary mb-3">Inside the platform</h2>
          <p className="body-medium mb-8 max-w-3xl">A look at the live application. Personal data (names, emails and internal URLs) has been blurred for this case study.</p>
          <div className="grid md:grid-cols-2 gap-8">
            {screens.map((screen, i) => (
              <div key={i} className="team-card overflow-hidden" data-testid={`case-screen-${i}`}>
                <a
                  href={screen.image}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block aspect-[16/10] bg-bg-light border-b border-border-medium flex items-center justify-center overflow-hidden group"
                >
                  <img
                    src={screen.image}
                    alt={screen.title}
                    className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-[1.02]"
                  />
                </a>
                <div className="p-6">
                  <h3 className="heading-6 text-text-primary mb-2">{i + 1}. {screen.title}</h3>
                  <p className="body-small text-text-secondary">{screen.caption}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Architecture & security */}
        <section className="mb-16">
          <h2 className="heading-3 text-text-primary mb-8">Architecture & security</h2>
          <div className="team-card p-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <div className="flex items-center gap-2 mb-3 text-brand-primary">
                  <Server size={18} />
                  <h3 className="heading-6 text-text-primary">Architecture</h3>
                </div>
                <ul className="space-y-2 body-small text-text-secondary">
                  <li><b className="text-text-primary">Frontend:</b> React SPA (dashboards, live org chart)</li>
                  <li><b className="text-text-primary">Backend:</b> FastAPI (REST, all routes under /api)</li>
                  <li><b className="text-text-primary">Database:</b> MongoDB (async via Motor); files in GridFS</li>
                </ul>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-3 text-brand-primary">
                  <ShieldCheck size={18} />
                  <h3 className="heading-6 text-text-primary">Security</h3>
                </div>
                <p className="body-small text-text-secondary">
                  JWT (python-jose) with bcrypt password hashing and role-based access control — Superadmin, Admin,
                  Department Head and Employee — each seeing and editing only what they should.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* APIs & integrations */}
        <section className="mb-16">
          <h2 className="heading-3 text-text-primary mb-8">APIs & integrations</h2>
          <h3 className="heading-6 text-brand-primary uppercase tracking-wide mb-4">Internal REST API (FastAPI)</h3>
          <div className="team-card overflow-hidden mb-8">
            {apiGroups.map(([k, v], i) => (
              <div key={i} className={`grid grid-cols-1 sm:grid-cols-4 gap-2 px-6 py-4 ${i !== apiGroups.length - 1 ? 'border-b border-border-light' : ''}`}>
                <span className="caption text-brand-primary">{k}</span>
                <span className="body-small text-text-secondary sm:col-span-3 font-mono text-[13px] break-words">{v}</span>
              </div>
            ))}
          </div>
          <h3 className="heading-6 text-brand-primary uppercase tracking-wide mb-4">External / third-party</h3>
          <div className="grid md:grid-cols-3 gap-6">
            {externals.map((e, i) => (
              <div key={i} className="team-card p-6">
                <div className="flex items-center gap-2 mb-2 text-brand-primary">
                  <GitBranch size={18} />
                  <h4 className="heading-6 text-text-primary">{e.title}</h4>
                </div>
                <p className="body-small text-text-secondary">{e.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Benefits */}
        <section className="mb-16">
          <h2 className="heading-3 text-text-primary mb-8">Benefits to the company</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => (
              <div key={i} className="team-card p-6">
                <div className="mb-3">{b.icon}</div>
                <h3 className="heading-6 text-text-primary mb-2">{b.title}</h3>
                <p className="body-small text-text-secondary">{b.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Tech stack */}
        <section className="mb-16">
          <h2 className="heading-3 text-text-primary mb-6">Technology stack</h2>
          <div className="flex flex-wrap gap-2">
            {stack.map((t, i) => (
              <span key={i} className="px-3 py-1.5 rounded-full bg-brand-soft text-brand-primary text-sm font-medium border border-indigo-100">{t}</span>
            ))}
          </div>
        </section>

        {/* Back CTA */}
        <div className="text-center">
          <Link to="/" className="btn-secondary inline-flex items-center gap-2" data-testid="case-back-bottom">
            <ArrowLeft size={18} />
            Back to portfolio
          </Link>
        </div>
      </main>
    </div>
  );
};

export default CaseStudyHR;
